import API_CONFIG, {
  AUTH_SESSION_EXPIRED_EVENT,
  getResolvedApiToken,
  getResolvedTenantId,
  getStoredAuthRole,
  isSessionExpirySuppressed,
} from '../../config/api-config';
import {
  ApiError,
  extractApiErrorMessage,
  logApiError,
  parseApiResponseBody,
} from '../../utils/apiError';
import { getActiveAbortSignal, isAbortError } from '../../utils/abortSignal';
import { isApiCallAllowed } from './apiAccessStore';
import type { ApiResponse } from './types/common';

export { ApiError } from '../../utils/apiError';

const LOGIN_ENDPOINTS = ['/auth/login/basic', '/super-admin/login', '/auth/oauth/'];

/** Extra options for {@link ApiClient.request} (not passed to `fetch`). */
export type ApiRequestOptions = RequestInit & {
  /** Super-admin: act on a specific tenant (sets `x-tenant-id` for this request). */
  tenantId?: string | number;
  /** Public auth endpoints — omit bearer token and tenant headers. */
  skipAuth?: boolean;
  /**
   * When true, a 401 does not clear the login session.
   * Use for super-admin calls that may fail auth on tenant-scoped routes
   * without meaning the platform session itself expired.
   */
  suppressSessionExpiry?: boolean;
};

export class ApiClient {
  baseUrl: string;

  constructor(baseUrl: string = API_CONFIG.BASE_URL) {
    this.baseUrl = baseUrl;
  }

  authHeaders(tenantIdOverride?: string | number): Record<string, string> {
    const headers: Record<string, string> = {};
    const token = getResolvedApiToken();
    const role = getStoredAuthRole();
    const tenantRaw =
      tenantIdOverride != null && String(tenantIdOverride).trim()
        ? String(tenantIdOverride).trim()
        : getResolvedTenantId();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    // Tenant APIs resolve school context from `x-tenant-id` when Host is `api.*`.
    // Always send a numeric tenant id for non–super-admin sessions (and for
    // super-admin calls that explicitly pass a tenant override).
    if (
      tenantRaw &&
      /^\d+$/.test(tenantRaw) &&
      (role !== 'super_admin' || tenantIdOverride != null)
    ) {
      headers['x-tenant-id'] = tenantRaw;
    }
    return headers;
  }

  private notifySessionExpired(endpoint: string, hadAuthHeader: boolean): void {
    if (!hadAuthHeader) return;
    if (LOGIN_ENDPOINTS.some((path) => endpoint.startsWith(path))) return;
    // Super-admin platform tokens are rejected by many tenant routes (401) without
    // the platform session itself being expired — never wipe that login from a 401.
    if (getStoredAuthRole() === 'super_admin') return;
    // Login / restoreSession wrap credential swaps so a parallel 401 cannot bounce
    // the user back to /login before the new token is fully established.
    if (isSessionExpirySuppressed()) return;
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new Event(AUTH_SESSION_EXPIRED_EVENT));
  }

  private buildHeaders(
    options: ApiRequestOptions,
    tenantId?: string | number,
    skipAuth = false
  ): Record<string, string> {
    const mergedHeaders: Record<string, string> = {
      Accept: 'application/json',
      ...API_CONFIG.DEFAULT_HEADERS,
      ...(skipAuth ? {} : this.authHeaders(tenantId)),
      ...(options.headers as Record<string, string>),
    };
    const method = String(options.method || 'GET').toUpperCase();
    const hasBody = options.body !== undefined && options.body !== null;
    if (!hasBody || method === 'GET' || method === 'HEAD') {
      delete mergedHeaders['Content-Type'];
      delete mergedHeaders['content-type'];
    }
    return mergedHeaders;
  }

  /**
   * Skip calls the user's DB role cannot make (rules from GET /users/me/api-access).
   * Throws the same 403 ApiError the server would return, without hitting the network.
   */
  private assertRoleMayCall(method: string, endpoint: string): void {
    if (isApiCallAllowed(method, endpoint)) return;
    throw new ApiError(403, 'Your role does not have access to this action.', {
      statusText: 'Forbidden (blocked client-side by role access rules)',
      endpoint,
      method,
    });
  }

  private throwApiError(
    response: Response,
    data: unknown,
    endpoint: string,
    method: string,
    hadAuthHeader: boolean,
    suppressSessionExpiry = false
  ): never {
    const msg = extractApiErrorMessage(response.status, data, response.statusText);
    if (response.status === 401 && !suppressSessionExpiry) {
      this.notifySessionExpired(endpoint, hadAuthHeader);
    }
    throw new ApiError(response.status, msg, {
      body: data,
      statusText: response.statusText,
      endpoint,
      method,
    });
  }

  async request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<ApiResponse<T>> {
    const { tenantId, skipAuth = false, suppressSessionExpiry = false, ...fetchOptions } = options;
    // Super-admin tenant override: a 401 usually means the tenant route rejected the
    // platform token, not that the super-admin login expired.
    const skipExpiry = suppressSessionExpiry || tenantId != null;
    const url = `${this.baseUrl}${endpoint}`;
    const method = String(fetchOptions.method || 'GET').toUpperCase();
    // Public endpoints and super-admin tenant overrides bypass the client-side gate.
    if (!skipAuth && tenantId == null) {
      this.assertRoleMayCall(method, endpoint);
    }
    const mergedHeaders = this.buildHeaders(options, tenantId, skipAuth);
    // Capture scoped signal synchronously (before any await) so concurrent useApi
    // calls each abort only their own in-flight request.
    const signal = fetchOptions.signal ?? getActiveAbortSignal();
    const config: RequestInit = {
      ...fetchOptions,
      headers: mergedHeaders,
      signal,
    };

    try {
      const response = await fetch(url, config);
      const data = await parseApiResponseBody(response);

      if (!response.ok) {
        this.throwApiError(
          response,
          data,
          endpoint,
          method,
          Boolean(mergedHeaders.Authorization),
          skipExpiry
        );
      }

      return {
        data: data as T,
        status: response.status,
        message: (data && typeof data === 'object' && (data as { message?: string }).message) || 'Success',
      };
    } catch (error) {
      if (!skipAuth && !isAbortError(error)) {
        logApiError(error, { url, method });
      }
      throw error;
    }
  }

  /** Multipart upload (do not set Content-Type — browser adds boundary). */
  async uploadForm<T>(
    endpoint: string,
    formData: FormData,
    tenantId?: string | number,
    signal?: AbortSignal
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const method = 'POST';
    if (tenantId == null) {
      this.assertRoleMayCall(method, endpoint);
    }
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...this.authHeaders(tenantId),
    };
    // Same as request(): tenant override must not wipe the super-admin session on 401.
    const skipExpiry = tenantId != null;
    const abortSignal = signal ?? getActiveAbortSignal();

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: formData,
        signal: abortSignal,
      });
      const data = await parseApiResponseBody(response);

      if (!response.ok) {
        this.throwApiError(
          response,
          data,
          endpoint,
          method,
          Boolean(headers.Authorization),
          skipExpiry
        );
      }

      return {
        data: data as T,
        status: response.status,
        message:
          (data && typeof data === 'object' && (data as { message?: string }).message) || 'Success',
      };
    } catch (error) {
      if (!isAbortError(error)) {
        logApiError(error, { url, method });
      }
      throw error;
    }
  }

  /** Salary slip PDF (binary). */
  async getSalarySlipPdf(staffId: string, period: string, signal?: AbortSignal): Promise<Blob> {
    const path = `/staff/salary-slip/${encodeURIComponent(staffId)}/${encodeURIComponent(period)}/pdf`;
    const url = `${this.baseUrl}${path}`;
    const abortSignal = signal ?? getActiveAbortSignal();
    const response = await fetch(url, {
      method: 'GET',
      headers: this.authHeaders(),
      signal: abortSignal,
    });
    if (!response.ok) {
      const data = await parseApiResponseBody(response);
      throw new ApiError(
        response.status,
        extractApiErrorMessage(response.status, data, response.statusText),
        {
          body: data,
          statusText: response.statusText,
          endpoint: path,
          method: 'GET',
        }
      );
    }
    return response.blob();
  }
}
